import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import './dialog.js';

/** Keep the small gift summary in sync with the theme's existing variant fetch. */
class GiftSelection extends Component {
  #abortController;
  #generation = 0;
  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#update, {
      signal: this.#abortController.signal,
    });
  }
  disconnectedCallback() {
    this.#abortController?.abort();
    ++this.#generation;
    super.disconnectedCallback();
  }
  #update = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const generation = ++this.#generation;
    this.setAttribute('aria-busy', 'true');
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.#generation || !detail?.html) return;
      if (detail.productId && String(detail.productId) !== this.dataset.productId) return;
      const replacement = detail.html.querySelector('tts-gift-selection');
      if (replacement) this.replaceChildren(...replacement.childNodes);
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[gift-selection] Unable to update summary', error);
    } finally {
      if (generation === this.#generation) this.removeAttribute('aria-busy');
    }
  };
}
if (!customElements.get('tts-gift-selection')) customElements.define('tts-gift-selection', GiftSelection);

/** Fetch hidden carousel photos only when revealed or approached by a swipe. */
class GiftGalleryImage extends Component {
  #mutation;
  #intersection;
  #content;
  connectedCallback() {
    super.connectedCallback();
    // Native variant updates can replace children while keeping this component mounted.
    this.#content = new MutationObserver(() => {
      if (!this.querySelector('template')) return;
      const currentSlide = this.closest('slideshow-slide');
      if (currentSlide) {
        this.#mutation?.observe(currentSlide, { attributes: true, attributeFilter: ['aria-hidden'] });
        this.#intersection?.observe(currentSlide);
        if (currentSlide.getAttribute('aria-hidden') === 'false') this.#load();
      } else {
        this.#intersection?.observe(this.closest('label') || this);
      }
    });
    this.#content.observe(this, { childList: true });
    const slide = this.closest('slideshow-slide');
    if (!slide) {
      this.#intersection = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.#load();
      }, { threshold: 0.01 });
      this.#intersection.observe(this.closest('label') || this);
      return;
    }
    this.#mutation = new MutationObserver(() => {
      if (slide.getAttribute('aria-hidden') === 'false') this.#load();
    });
    this.#mutation.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
    this.#intersection = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) this.#load();
    }, { root: slide.closest('slideshow-slides'), threshold: 0.01 });
    this.#intersection.observe(slide);
    if (slide.getAttribute('aria-hidden') === 'false') this.#load();
  }
  disconnectedCallback() {
    this.#mutation?.disconnect();
    this.#intersection?.disconnect();
    this.#content?.disconnect();
    super.disconnectedCallback();
  }
  #load() {
    const template = this.querySelector('template');
    if (!template) return;
    this.append(template.content.cloneNode(true));
    template.remove();
    this.#mutation?.disconnect();
    this.#intersection?.disconnect();
  }
}
if (!customElements.get('tts-gallery-image')) customElements.define('tts-gallery-image', GiftGalleryImage);
